import {useEffect, useRef, useState} from "react";
import ReactQuill from "react-quill";
import { markdownToHtml, htmlToMarkdown } from "./Parser";
import "react-quill/dist/quill.snow.css";
import { styled } from "styled-components";
import { FlexAlignCenter, FlexJustifyBetweenAlignCenter } from "@/components/ui/FlexBox.ts";
import { Button } from "@/components/ui/Button.tsx";
import { NumericList, DotList } from "@/pages/assistants/FilesInScenario/icons.tsx";
import { ToolbarButton } from "@/pages/assistants/FilesInScenario/styles.ts";

const StyledEditor = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;

    .ql-container.ql-snow {
        border-radius: 3px;
    }
    p {
        font-size: 14px;
    }
    h3 {
        font-size: 16px !important;
        font-weight: 400;
    }
    h2 {
        font-size: 18px !important;
        font-weight: 400;
    }
    h1 {
        font-size: 22px !important;
        font-weight: 400;
    }
`;

const SelectFontContainer = styled.div`
    height: 40px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #CECECE;
    display: flex;
    width: 105px;
    
    div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 35px;
        border-left: 1px solid #CECECE;
        border-right: 1px solid #CECECE;
    }
`

const SelectFontButton = styled.button`
    width: 35px;
    
    background: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 700;
    font-size: 16px;
    &:disabled {
        cursor: not-allowed;
    }
`

const StyledToolbar = styled.div`
    margin-left: 30px;
    display: flex;
    button + button {
        margin-left: 10px;
    }
`

interface IDocEditorProps {
    onSave: (value: string) => void,
    initialValue?: string,
    isLoading: boolean
}

export interface EditorContentChanged {
    html: string;
    markdown: string;
}

export interface EditorProps {
    value?: string;
    onChange?: (changes: EditorContentChanged) => void;
}

const fontSizes = {
    "14": 0,
    "22": 1,
    "18": 2,
    "16": 3
}

type TFontSizes = '14' | '16' | '18' | '22';

const fontSizeOrder: TFontSizes[] = ["14", "16", "18", "22"];

const MAX_LENGTH = 25000;

export const DocEditor = ({ onSave, initialValue, isLoading }: IDocEditorProps) => {
    const [value, setValue] = useState<string>('');
    const [textLength, setTextLength] = useState(0);
    const [selectedFontSize, setSelectedFontSize] = useState<TFontSizes>("14");
    const markDown = useRef('');
    const reactQuillRef = useRef<ReactQuill>(null);


    const onChange = (content: string) => {
        const unprivilegedEditor = reactQuillRef.current?.unprivilegedEditor;
        if (unprivilegedEditor && unprivilegedEditor.getLength() <= MAX_LENGTH) {
            setTextLength(unprivilegedEditor.getLength())
            setValue(content);
            markDown.current = htmlToMarkdown(content);
        }
    };

    const handleHeaderAdd = () => {
        const quill = reactQuillRef.current?.getEditor();
        const index = fontSizeOrder.indexOf(selectedFontSize);
        const nextSize = fontSizeOrder[index + 1];
        if (nextSize) {
            setSelectedFontSize(nextSize);
            quill?.format('header', fontSizes[nextSize as keyof typeof fontSizes]);
        }
    };

    const handleHeaderReduce = () => {
        const quill = reactQuillRef.current?.getEditor();
        const index = fontSizeOrder.indexOf(selectedFontSize);
        const prevSize = fontSizeOrder[index - 1];
        if (prevSize) {
            setSelectedFontSize(prevSize);
            quill?.format('header', fontSizes[prevSize as keyof typeof fontSizes]);
        }
    };

    const handleBold = () => {
        const quill = reactQuillRef.current?.getEditor();
        const isBold = quill?.getFormat().bold;
        quill?.format("bold", !isBold);
    };
    const handleSelectionChange = () => {
        const quill = reactQuillRef.current?.getEditor();
        if (quill) {
            const format = quill.getFormat();
            if(format.header) {
                const size = Object.keys(fontSizes).find((key => fontSizes[key as TFontSizes] === format.header));
                setSelectedFontSize(size as TFontSizes)
            } else {
                setSelectedFontSize("14")
            }
        }
    }

    const handleOrderedList = () => {
        const quill = reactQuillRef.current?.getEditor();
        if(quill) {
            const format = quill.getFormat();
            if (format.list !== "ordered") {
                quill.format("list", "ordered");
            } else {
                quill.format("list", false); // убираем список
            }
        }
    };

    const handleBulletList = () => {
        const quill = reactQuillRef.current?.getEditor();
        if (quill) {
            const format = quill.getFormat();
            if (format.list !== "bullet") {
                quill.format("list", "bullet");
            } else {
                quill.format("list", false);
            }
        }
    };

    const handleSave = () => {
        const removedEscaping = markDown.current
            .replace(/\\/g, "")
            .replace(/\n\n/g, "\n")
            .replace(/\n+$/, "");

        onSave(removedEscaping);
    };

    useEffect(() => {
        if(initialValue) {
            setValue(markdownToHtml(initialValue))
        }
    }, [initialValue]);

    return (
        <StyledEditor>
            <FlexJustifyBetweenAlignCenter style={{ marginBottom: '20px' }}>
                <FlexAlignCenter>
                    <SelectFontContainer>
                        <SelectFontButton
                            onClick={handleHeaderReduce}
                            disabled={selectedFontSize === fontSizeOrder[0]}
                        >
                            -
                        </SelectFontButton>
                        <div>{selectedFontSize}</div>
                        <SelectFontButton
                            onClick={handleHeaderAdd}
                            disabled={selectedFontSize === fontSizeOrder[fontSizeOrder.length - 1]}
                        >
                            +
                        </SelectFontButton>
                    </SelectFontContainer>
                    <StyledToolbar>
                        <ToolbarButton onClick={handleBold}>
                            B
                        </ToolbarButton>
                        <ToolbarButton onClick={handleOrderedList}>
                            <NumericList/>
                        </ToolbarButton>
                        <ToolbarButton onClick={handleBulletList}>
                            <DotList />
                        </ToolbarButton>
                    </StyledToolbar>
                </FlexAlignCenter>
                <FlexAlignCenter>
                    <div style={{marginRight: '22px'}}>
                        {textLength} / {MAX_LENGTH}
                    </div>
                    <Button disabled={isLoading} onClick={handleSave} variant="primary">
                        Сохранить
                    </Button>
                </FlexAlignCenter>
            </FlexJustifyBetweenAlignCenter>
            <ReactQuill
                ref={reactQuillRef}
                onChangeSelection={handleSelectionChange}
                modules={{
                    toolbar: false,
                }}
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    boxSizing: "border-box",
                    wordBreak: "break-word",
                    background: '#fff',
                    borderRadius: '3px'
                }}
                value={value}
                onChange={onChange}
            />
        </StyledEditor>
    );
};
